import { useSelector } from "react-redux";
import { RootState } from "../store";
import { IRootReducerState } from "../store/IRootReducer";
import { useGetAccessibleCommunitiesQuery } from "../store/api/crmCommunitiesApi";

/**
 * What the community-tz gate resolved to. `tz` is the value tz-YES surfaces pass to
 * useDisplayTz; `communityName` is the community it came FROM, carried so the persistent
 * active-zone indicator can say WHOSE clock the app is on without re-deriving the gate.
 */
export interface CommunityTzContext {
  /** The community's IANA zone, or undefined -> fall through to the personal chain. */
  tz: string | undefined;
  /** Name of the community whose zone won. Defined only when `tz` is. */
  communityName: string | undefined;
}

/**
 * The IANA zone to use for tz-YES surfaces under the CORRECTED model, where the Personal/Community
 * tz MODE (user_preferences.timezone_mode) is the zone-DECIDER and the community FILTER only scopes
 * data. Returns:
 *   - undefined in PERSONAL mode (ALWAYS - even when a community filter is applied) -> personal zone
 *   - undefined in COMMUNITY mode + "All communities" (no single active community) -> personal floor
 *   - the selected community's zone in COMMUNITY mode + a specific community (or undefined if it has
 *     no stored zone -> personal)
 *
 * The ALL-COMMUNITIES fallback to personal is RATIFIED (#921 ruling addendum, 2026-08-17), not an
 * accident: the rejected alternative was per-row community zones, which mixes zones inside one list
 * and forces per-row zone labels — explicitly out of scope. It is keyed on whether a SINGLE
 * COMMUNITY IS ACTIVE in the top-bar selector, NOT on how many communities the loaded data spans.
 *
 * OPT-IN seam: tz-YES surfaces call useDisplayTz(useCommunityTz()); undefined -> useDisplayTz falls
 * through to the personal chain. tz-NO surfaces never call this, so they are untouched.
 *
 * Reads the SAME accessible-communities query the header selector populates (RTK Query dedupes it),
 * keyed on the globally-selected instance. The request is SKIPPED unless the mode is 'community' AND
 * an instance AND a community are selected, so personal-mode / Marketing / non-CRM surfaces never
 * fire the CRM-only request.
 *
 * This is the ONE implementation of the gate; useCommunityTz() is a thin projection of it, so the
 * zone a surface renders in and the zone the indicator advertises cannot drift apart.
 */
export function useCommunityTzContext(): CommunityTzContext {
  const mode = useSelector((s: RootState) => s.userPreferencesReducer.userTimezoneMode);
  const selectedInstanceId = useSelector(
    (s: IRootReducerState) => s.instancesReducer?.instancesData?.selectedInstanceId,
  );
  const selectedCommunityId = useSelector((s: RootState) => s.communityReducer.selectedCommunityId);
  const { data } = useGetAccessibleCommunitiesQuery(
    { instanceId: selectedInstanceId ?? "" },
    { skip: mode !== "community" || !selectedInstanceId || !selectedCommunityId },
  );
  // PERSONAL mode -> always personal zone, EVEN when filtered to a community (the corrected model).
  if (mode !== "community") return { tz: undefined, communityName: undefined };
  // COMMUNITY mode + All communities -> no single active community -> personal floor.
  if (!selectedCommunityId) return { tz: undefined, communityName: undefined };
  const community = data?.communities.find((c) => c.id === selectedCommunityId);
  // A community with NO stored zone falls through to personal, so it must NOT be named as the
  // source either — name and zone are reported together or not at all.
  if (!community?.timezone) return { tz: undefined, communityName: undefined };
  return { tz: community.timezone, communityName: community.name };
}

/** The community zone for a tz-YES surface: `useDisplayTz(useCommunityTz())`. */
export function useCommunityTz(): string | undefined {
  return useCommunityTzContext().tz;
}
